/**
 * The console laws, the icon set and the keyboard map — verbatim from the
 * prototype (`docs/design/reference/app-source.js`).
 *
 * The laws are not documentation. Each screen declares which ones it is
 * answerable to and the header renders them as chips, so the constraint is
 * visible at the place the work happens. They restate CLAUDE.md Part II in the
 * console's own vocabulary; where the two differ, CLAUDE.md wins and this file
 * is the bug.
 */

/** `[short name, what it means]`, keyed by law number. */
export const LAWS: Readonly<Record<number, readonly [short: string, text: string]>> = {
  1: ['no freeze', 'frozen is EMI-imposed: displayed with attribution, never settable here'],
  2: ['instruction not action', 'money-shaped actions are instructions to FinXP with an idempotency key and a provider reference'],
  3: ['no ledger edit', 'journal rows have no edit or delete; only Post correction'],
  4: ['available = min()', 'available is shown as a min() of ledger and provider figures, with the binding leg marked'],
  5: ['append-only', 'no write affordance on immutable tables'],
  6: ['new version', 'versioned entities edit by creating a version, with a diff and an effective_from'],
  7: ['no retro history', 'backfills are named, previewed, logged jobs; statements get new versions'],
  8: ['currency always', 'every amount carries its ISO-4217 code; scale from the exponent table'],
  9: ['minor units', 'no float in any displayed value, input or export'],
  10: ['no card data', 'no PAN, CVV or last-4 anywhere in this console'],
  11: ['no credentials', 'credential config shows status only, never a value or a masked value'],
  12: ['source badge', 'analytics is badged with freshness; money paths are ledger-only'],
  13: ['decision_ref', 'every refusal surfaces a copyable decision_ref into the explorer'],
  14: ['four outcomes', 'allow / review / step_up / refuse_to_submit — a closed vocabulary'],
  15: ['maker-checker', 'two visible slots; the checker slot is disabled for the maker'],
  16: ['locks outrank', 'compliance locks render above ops settings and need the elevated role'],
  17: ['no promote here', 'feedback and per-user mappings have no promote path at all'],
  18: ['cell aware', 'cell id and role are always visible; standby disables mutating controls'],
  19: ['stale not down', 'outbox depth and reconciler lag are stale reporting, never an outage']
}

/**
 * The seven operator roles.
 *
 * Presentation only — the console shows what a role may do and the backend
 * decides. A role switcher that widened access would be a bug in go-wallet,
 * not a convenience here (CLAUDE.md Part I §5).
 */
export const ROLES: readonly string[] = ['Support', 'Ops', 'Compliance', 'Finance', 'Growth', 'Engineering', 'Admin']

/**
 * Per-section nav glyphs, as raw SVG path data in nav order. Up to four paths
 * each; the renderer emits every slot and lets the empty ones draw nothing.
 */
export const SEC_ICONS: readonly (readonly string[])[] = [
  ['m12 14 4-4', 'M3.34 19a10 10 0 1 1 17.32 0'],
  ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2', 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0'],
  ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0', 'm16 11 2 2 4-4'],
  ['M8 3 4 7l4 4', 'M4 7h16', 'm16 21 4-4-4-4', 'M20 17H4'],
  ['M12 7v14', 'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z'],
  ['m21 21-4.34-4.34', 'M19 11a8 8 0 1 1-16 0 8 8 0 0 1 16 0'],
  ['M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z'],
  // Handles (#111), INSERTED at index 7 rather than appended. The rail reads
  // this list positionally against `NAV`, so an icon added at the end would have
  // shifted every section from Pricing onwards onto the wrong glyph — a change
  // nothing in the suite catches, because the only assertion is on the length.
  // An at-sign: a handle IS the `@name` on a creator's pay-me link.
  ['M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0', 'M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8'],
  ['M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z', 'M7.5 8a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1'],
  ['M9 17H7A5 5 0 0 1 7 7h2', 'M15 7h2a5 5 0 1 1 0 10h-2', 'M8 12h8'],
  ['M12 22v-5', 'M9 8V2', 'M15 8V2', 'M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z'],
  ['M16 10a4 4 0 0 1-8 0', 'M3.103 6.034h17.794', 'M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 15 2H9a2 2 0 0 0-1.6.8z'],
  ['M3 3v16a2 2 0 0 0 2 2h16', 'M18 17V9', 'M13 17V5', 'M8 17v-3'],
  ['M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z', 'M14 2v4a2 2 0 0 0 2 2h4', 'M16 13H8', 'M16 17H8'],
  ['m5 8 6 6', 'm4 14 6-6 2-3', 'M2 5h12', 'm22 22-5-10-5 10'],
  ['M20 7h-9', 'M14 17H5', 'M20 17a3 3 0 1 1-6 0 3 3 0 0 1 6 0', 'M10 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0'],
  ['M22 12H2', 'm5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z', 'M6 16h.01', 'M10 16h.01'],
  ['M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2Z', 'M7 11V7a5 5 0 0 1 10 0v4'],
  ['M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.412-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-5.776-4.719']
]

/**
 * Severity glyphs. Only r / a / g / v carry one — neutral is deliberately
 * unmarked, so an icon appearing in a row always means something needs reading.
 */
export const TONE_ICON: Readonly<Record<string, readonly string[]>> = {
  r: ['M12 16h.01', 'M12 8v4', 'M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z'],
  a: ['M12 9v4', 'M12 17h.01', 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3'],
  g: ['M20 6 9 17l-5-5'],
  v: ['M3 3v16a2 2 0 0 0 2 2h16', 'M18 17V9', 'M13 17V5']
}

export const COPY_ICON: readonly [string, string] = ['M9 9h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2z', 'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1']

/** `[keys, what it does]`. Shown in the `?` overlay. */
export const SHORTCUTS: readonly (readonly [keys: string, does: string])[] = [
  ['j / k', 'Next / previous row'], ['↵', 'Open selected row in inspector'], ['⌘K', 'Search and command palette'],
  ['>', 'Command mode inside the palette'], ['?', 'This overlay'], ['t', 'Toggle theme'], ['d', 'Toggle density'],
  ['[', 'Collapse navigation'], [']', 'Collapse inspector'], ['g then d', 'Go to dashboard'], ['1–5', 'Preview screen state'], ['esc', 'Close overlay']
]

/**
 * The five states every screen can be previewed in.
 *
 * Loading, empty, error and denied are designed states, not fallbacks. An
 * operator who cannot tell an empty queue from a broken screen will treat both
 * the same way, and one of those is an incident.
 */
export const STATES: readonly (readonly [id: ScreenState, label: string])[] = [['populated', 'Populated'], ['loading', 'Loading'], ['empty', 'Empty'], ['error', 'Error'], ['denied', 'Denied']]

export type ScreenState = 'populated' | 'loading' | 'empty' | 'error' | 'denied'
